import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { PageHero } from "@/components/site/PageHero";
import { ServicePicker } from "@/components/site/ServicePicker";
import { SelectInput, TextArea, TextInput, Toggle } from "@/components/site/Field";
import { WhatsAppIcon } from "@/components/site/WhatsAppFloat";
import { api, brl, errMsg, todayISO } from "@/lib/api";
import { ATENDIMENTO, DESLOCAMENTO, LOCAIS, PAGAMENTO, waLink } from "@/lib/content";
import { osWhatsappText } from "@/lib/whatsapp";

const STEPS = ["Serviços", "Cliente", "Veículo", "Atendimento", "Revisão"];
const C0 = { nome: "", telefone: "", whatsapp: "", email: "", endereco: "", cidade: "", estado: "", cep: "" };
const V0 = { marca: "", modelo: "", ano: "", versao: "", placa: "", cor: "", quilometragem: "", origem: "Nacional", blindado: false, eletrico: false, hibrido: false };
const A0 = { local_atendimento: "", data_pretendida: "", forma_pagamento: "", observacoes: "", danos_declarados: "" };

const foraRJ = (c) => !["rio de janeiro", "rj"].includes((c || "").trim().toLowerCase());

const Stepper = ({ step }) => (
  <ol className="mb-10 grid grid-cols-5 gap-1" data-testid="quote-stepper">
    {STEPS.map((s, i) => (
      <li key={s} className={`border-t-2 pt-2 ${i <= step ? "border-red-600" : "border-slate-700"}`}>
        <span className={`font-mono text-[10px] ${i <= step ? "text-red-400" : "text-slate-600"}`}>0{i + 1}</span>
        <span className={`hidden font-heading text-xs font-bold uppercase sm:block ${i === step ? "text-white" : "text-slate-500"}`}>{s}</span>
      </li>
    ))}
  </ol>
);

const Summary = ({ lines }) => {
  const sum = lines.reduce((a, l) => a + (l.preco || 0), 0);
  const pend = lines.some((l) => l.preco == null);
  return (
    <aside className="card-tech h-fit p-6 lg:sticky lg:top-32" data-testid="quote-summary">
      <p className="eyebrow mb-4">Resumo</p>
      {lines.length === 0 && <p className="text-sm text-slate-500">Nenhum serviço selecionado.</p>}
      <ul className="space-y-2">
        {lines.map((l) => (
          <li key={l.key} className="flex justify-between gap-3 text-sm">
            <span className="text-slate-300"><b className="font-mono text-red-400">{l.code}</b> {l.nome}</span>
            <span className="shrink-0 font-mono text-xs text-slate-400">{brl(l.preco)}</span>
          </li>
        ))}
      </ul>
      <div className="mt-5 border-t border-slate-700 pt-4">
        <div className="flex justify-between text-sm text-slate-400"><span>Deslocamento</span><span>A definir pelo prestador</span></div>
        <div className="mt-2 flex justify-between font-heading text-lg font-bold text-white"><span>Serviços</span><span data-testid="quote-subtotal">{brl(sum)}</span></div>
        {pend && <p className="mt-2 text-xs text-amber-500">Há itens sob consulta: o prestador definirá os valores.</p>}
      </div>
    </aside>
  );
};

const Success = ({ os }) => {
  const link = `${window.location.origin}/os/${os.share_token}`;
  return (
    <div className="card-tech mx-auto max-w-2xl p-8 text-center" data-testid="quote-success">
      <CheckCircle2 className="mx-auto h-12 w-12 text-red-500" />
      <p className="eyebrow mt-4">Ordem de serviço gerada</p>
      <p className="mt-2 font-mono text-4xl font-bold text-white" data-testid="quote-success-numero">{os.numero}</p>
      <p className="mt-4 text-slate-400">Envie a solicitação ao WhatsApp do Nillo. O prestador definirá o custo de deslocamento e o valor total; depois disso o aceite digital ficará disponível no link da sua OS.</p>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <a href={waLink(osWhatsappText(os, link))} target="_blank" rel="noreferrer" className="btn-red" data-testid="quote-send-whatsapp"><WhatsAppIcon className="h-4 w-4" /> Enviar ao WhatsApp</a>
        <Link to={`/os/${os.share_token}`} className="btn-line" data-testid="quote-view-os"><FileText className="h-4 w-4" /> Ver ordem de serviço</Link>
      </div>
    </div>
  );
};

export default function Orcamento() {
  const { config, itemsByKey, sel, toggle, clearSel } = useApp();
  const [step, setStep] = useState(0);
  const [c, setC] = useState(C0);
  const [v, setV] = useState(V0);
  const [a, setA] = useState(A0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const lines = useMemo(() => sel.map((k) => itemsByKey[k]).filter(Boolean), [sel, itemsByKey]);
  const sc = (k) => (x) => setC((s) => ({ ...s, [k]: x }));
  const sv = (k) => (x) => setV((s) => ({ ...s, [k]: x }));
  const sa = (k) => (x) => setA((s) => ({ ...s, [k]: x }));

  const valid = [
    () => lines.length > 0 || "Selecione ao menos um serviço",
    () => !!(c.nome && c.telefone && c.endereco && c.cidade && c.estado) || "Preencha nome, telefone, endereço, cidade e estado",
    () => !!(v.marca && v.modelo) || "Informe marca e modelo do veículo",
    () => !!a.forma_pagamento || "Escolha a forma de pagamento",
    () => true,
  ];
  const next = () => {
    const r = valid[step]();
    if (r !== true) return toast.error(r);
    setStep(step + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/quotes", { cliente: c, veiculo: v, servicos: sel, ...a });
      setDone(data);
      clearSel();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (done) return <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6"><Success os={done} /></section>;

  return (
    <div data-testid="page-orcamento">
      <PageHero eyebrow="Orçamento personalizado" title="Monte seu orçamento">Selecione os serviços numerados, informe seus dados e do veículo. O sistema gera a descrição operacional e a ordem de serviço automaticamente.</PageHero>
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
        <Stepper step={step} />
        <div className="grid gap-8 lg:grid-cols-[1fr_340px]">
          <div className="card-tech p-6 sm:p-8">
            {step === 0 && config && <ServicePicker items={config.items} groups={config.groups} selected={sel} onToggle={toggle} />}
            {step === 1 && (
              <div className="grid gap-4 sm:grid-cols-2" data-testid="quote-step-cliente">
                <TextInput label="Nome completo" required value={c.nome} onChange={sc("nome")} testid="q-cliente-nome" className="sm:col-span-2" />
                <TextInput label="Telefone" required value={c.telefone} onChange={sc("telefone")} testid="q-cliente-telefone" />
                <TextInput label="WhatsApp" value={c.whatsapp} onChange={sc("whatsapp")} testid="q-cliente-whatsapp" />
                <TextInput label="E-mail" type="email" value={c.email} onChange={sc("email")} testid="q-cliente-email" className="sm:col-span-2" />
                <TextInput label="Endereço" required value={c.endereco} onChange={sc("endereco")} testid="q-cliente-endereco" className="sm:col-span-2" />
                <TextInput label="Cidade" required value={c.cidade} onChange={sc("cidade")} testid="q-cliente-cidade" />
                <div className="grid grid-cols-2 gap-4">
                  <TextInput label="Estado" required maxLength={2} value={c.estado} onChange={sc("estado")} testid="q-cliente-estado" />
                  <TextInput label="CEP" value={c.cep} onChange={sc("cep")} testid="q-cliente-cep" />
                </div>
                <p className="text-xs text-slate-500 sm:col-span-2">{DESLOCAMENTO.observacao} {DESLOCAMENTO.domicilio}</p>
              </div>
            )}
            {step === 2 && (
              <div className="grid gap-4 sm:grid-cols-2" data-testid="quote-step-veiculo">
                <TextInput label="Marca" required value={v.marca} onChange={sv("marca")} testid="q-veiculo-marca" />
                <TextInput label="Modelo" required value={v.modelo} onChange={sv("modelo")} testid="q-veiculo-modelo" />
                <TextInput label="Ano" value={v.ano} onChange={sv("ano")} testid="q-veiculo-ano" />
                <TextInput label="Versão" value={v.versao} onChange={sv("versao")} testid="q-veiculo-versao" />
                <TextInput label="Placa" value={v.placa} onChange={sv("placa")} testid="q-veiculo-placa" />
                <TextInput label="Cor" value={v.cor} onChange={sv("cor")} testid="q-veiculo-cor" />
                <TextInput label="Quilometragem" value={v.quilometragem} onChange={sv("quilometragem")} testid="q-veiculo-km" />
                <SelectInput label="Nacional ou importado" value={v.origem} onChange={sv("origem")} options={["Nacional", "Importado"]} testid="q-veiculo-origem" />
                <div className="grid gap-2 sm:col-span-2 sm:grid-cols-3">
                  <Toggle label="Blindado" checked={v.blindado} onChange={sv("blindado")} testid="q-veiculo-blindado" />
                  <Toggle label="Elétrico" checked={v.eletrico} onChange={sv("eletrico")} testid="q-veiculo-eletrico" />
                  <Toggle label="Híbrido" checked={v.hibrido} onChange={sv("hibrido")} testid="q-veiculo-hibrido" />
                </div>
              </div>
            )}
            {step === 3 && (
              <div className="grid gap-4 sm:grid-cols-2" data-testid="quote-step-atendimento">
                <SelectInput label="Local de atendimento" value={a.local_atendimento} onChange={sa("local_atendimento")} options={LOCAIS} testid="q-local" />
                <TextInput label="Data pretendida" type="date" min={todayISO()} value={a.data_pretendida} onChange={sa("data_pretendida")} testid="q-data-pretendida" />
                <SelectInput label="Forma de pagamento" required value={a.forma_pagamento} onChange={sa("forma_pagamento")} options={PAGAMENTO.opcoes} testid="q-pagamento" className="sm:col-span-2" />
                <p className="text-xs text-slate-500 sm:col-span-2">Bancos e bandeiras: {PAGAMENTO.bancos.join(", ")}. Fora do município do Rio de Janeiro: sinal de {PAGAMENTO.fora.sinal}, saldo {PAGAMENTO.fora.saldo.toLowerCase()}.</p>
                <TextArea label="Danos previamente declarados" value={a.danos_declarados} onChange={sa("danos_declarados")} testid="q-danos" className="sm:col-span-2" />
                <TextArea label="Observações" value={a.observacoes} onChange={sa("observacoes")} testid="q-observacoes" className="sm:col-span-2" />
                <div className="border border-slate-700 bg-black/30 p-4 text-xs leading-relaxed text-slate-400 sm:col-span-2">
                  <b className="text-slate-200">Condições do local:</b> {ATENDIMENTO.condicoes_local.join(", ")}. Preferencialmente em {ATENDIMENTO.preferencial.join(", ").toLowerCase()}. {ATENDIMENTO.domicilio} {ATENDIMENTO.observacao}
                </div>
              </div>
            )}
            {step === 4 && (
              <div data-testid="quote-step-revisao" className="space-y-6">
                <div>
                  <p className="eyebrow mb-3">Descrição operacional (gerada automaticamente)</p>
                  <ol className="list-decimal space-y-2 pl-5 text-sm leading-relaxed text-slate-300" data-testid="quote-descricao-operacional">
                    {lines.map((l) => <li key={l.key}>{l.operacional}</li>)}
                  </ol>
                </div>
                <div className="grid gap-2 text-sm text-slate-300 sm:grid-cols-2">
                  <p><b className="text-white">Cliente:</b> {c.nome} — {c.telefone}</p>
                  <p><b className="text-white">Veículo:</b> {v.marca} {v.modelo} {v.ano}</p>
                  <p><b className="text-white">Cidade:</b> {c.cidade}/{c.estado}</p>
                  <p><b className="text-white">Pagamento:</b> {a.forma_pagamento}</p>
                </div>
                {foraRJ(c.cidade) && <p className="border-l-2 border-red-600 bg-red-950/30 p-3 text-sm text-red-200" data-testid="quote-fora-municipio">Atendimento fora do município do Rio de Janeiro: pode haver custo de deslocamento e sinal de 20%.</p>}
                <p className="text-xs text-slate-500">O aceite digital ocorre somente depois que o prestador apresentar o valor total (incluindo deslocamento) no link da sua ordem de serviço.</p>
              </div>
            )}
            <div className="mt-10 flex justify-between gap-3 border-t border-slate-800 pt-6">
              <button type="button" disabled={step === 0} onClick={() => setStep(step - 1)} className="btn-line disabled:opacity-40" data-testid="quote-wizard-step-back"><ArrowLeft className="h-4 w-4" /> Voltar</button>
              {step < 4 ? (
                <button type="button" onClick={next} className="btn-red" data-testid="quote-wizard-step-next">Avançar <ArrowRight className="h-4 w-4" /></button>
              ) : (
                <button type="button" onClick={submit} disabled={busy} className="btn-red" data-testid="quote-submit-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Gerar ordem de serviço</button>
              )}
            </div>
          </div>
          <Summary lines={lines} />
        </div>
      </section>
    </div>
  );
}
